import { useEffect, useId } from "react";
import type { RadioCaptureHandle } from "../hooks/useRadioCapture";
import type { MqttConnectionStatus } from "../types/telemetry";
import {
  RADIO_LABEL_MAX_LENGTH,
  RADIO_METER_FLOOR_DB,
  RADIO_THRESHOLD_MAX_DB,
  RADIO_THRESHOLD_MIN_DB,
  type RadioCaptureState,
  type RadioTranscript,
} from "../types/radio";
import { TranscriptionLog } from "./TranscriptionLog";

interface RadioPanelProps {
  /** Only bind global keyboard shortcuts while the hosting drawer is open. */
  shortcutsEnabled?: boolean;
  capture: RadioCaptureHandle;
  transcripts: RadioTranscript[];
  mqttStatus: MqttConnectionStatus;
  onClearTranscripts?: () => void;
}

const STATE_COPY: Record<RadioCaptureState, { label: string; tone: string }> = {
  idle: { label: "Off", tone: "idle" },
  connecting: { label: "Connecting…", tone: "pending" },
  listening: { label: "Listening", tone: "live" },
  reconnecting: { label: "Reconnecting…", tone: "pending" },
  error: { label: "Error", tone: "error" },
};

function meterPercent(db: number): number {
  if (!Number.isFinite(db)) return 0;
  return Math.max(0, Math.min(100, ((db - RADIO_METER_FLOOR_DB) / -RADIO_METER_FLOOR_DB) * 100));
}

function formatDb(db: number): string {
  return Number.isFinite(db) ? `${db.toFixed(0)} dBFS` : "—";
}

export function RadioPanel({
  shortcutsEnabled = true,
  capture,
  transcripts,
  mqttStatus,
  onClearTranscripts,
}: RadioPanelProps) {
  const ids = {
    device: useId(),
    label: useId(),
    threshold: useId(),
    thresholdLabel: useId(),
  };
  const { enabled, start, stop } = capture;

  useEffect(() => {
    if (!shortcutsEnabled) return;
    function handleKeyDown(e: KeyboardEvent) {
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA" || e.key !== " ") return;
      e.preventDefault();
      if (enabled) stop();
      else void start();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [shortcutsEnabled, enabled, start, stop]);

  const stateCopy = STATE_COPY[capture.state];
  const level = meterPercent(capture.levelDb);
  const thresholdPercent = meterPercent(capture.thresholdDb);
  const busy = capture.state === "connecting";

  return (
    <div className="radio-panel">
      <div className="radio-panel__header">
        <h2 className="radio-panel__title">Radio</h2>
        <span
          className={`radio-state radio-state--${stateCopy.tone}`}
          role="status"
          aria-live="polite"
        >
          <span className="radio-state__dot" aria-hidden="true" />
          {stateCopy.label}
        </span>
      </div>

      {!capture.supported && (
        <div className="radio-notice radio-notice--error" role="alert">
          <strong>Microphone unavailable.</strong> Browsers only expose audio
          input on a secure page. Open the dashboard over <code>https://</code>{" "}
          or from <code>localhost</code>, then reload.
        </div>
      )}

      {capture.supported && capture.error && (
        <div className="radio-notice radio-notice--error" role="alert">
          {capture.error}
        </div>
      )}

      {capture.supported && !capture.error && capture.devices.length === 0 && (
        <div className="radio-notice">
          No audio inputs detected yet. Plug the handheld's audio cable into the
          laptop, then press Start — the browser will ask for microphone
          permission and the device list will fill in.
        </div>
      )}

      {mqttStatus !== "connected" && (
        <div className="radio-notice radio-notice--warn">
          MQTT is {mqttStatus}. Transcripts from other dashboards will not
          arrive until it reconnects.
        </div>
      )}

      <div className="radio-panel__controls">
        <label className="radio-field" htmlFor={ids.device}>
          <span className="radio-field__label">Input device</span>
          <select
            id={ids.device}
            className="radio-field__control"
            value={capture.selectedDeviceId}
            onChange={(e) => capture.selectDevice(e.target.value)}
            disabled={!capture.supported || capture.enabled}
          >
            <option value="">System default</option>
            {capture.devices.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label}
              </option>
            ))}
          </select>
        </label>

        <label className="radio-field" htmlFor={ids.label}>
          <span className="radio-field__label">Label</span>
          <input
            id={ids.label}
            className="radio-field__control"
            type="text"
            value={capture.label}
            maxLength={RADIO_LABEL_MAX_LENGTH}
            placeholder="pit"
            onChange={(e) => capture.setLabel(e.target.value)}
            disabled={capture.enabled}
            autoComplete="off"
            spellCheck={false}
          />
        </label>
      </div>

      <div className="radio-meter">
        <div className="radio-meter__header">
          <span className="radio-meter__label">Level</span>
          <span className="radio-meter__value">{formatDb(capture.levelDb)}</span>
          <span
            className={`radio-meter__gate${capture.vadOpen ? " radio-meter__gate--open" : ""}`}
          >
            {capture.vadOpen ? "Gate open" : "Gate closed"}
          </span>
        </div>
        <div className="radio-meter__track">
          <div
            className="radio-meter__fill"
            role="meter"
            aria-label="Input level"
            aria-valuemin={RADIO_METER_FLOOR_DB}
            aria-valuemax={0}
            aria-valuenow={Number.isFinite(capture.levelDb) ? Math.round(capture.levelDb) : RADIO_METER_FLOOR_DB}
            aria-valuetext={formatDb(capture.levelDb)}
            style={{ width: `${level}%` }}
          />
          <div
            className="radio-meter__threshold"
            style={{ left: `${thresholdPercent}%` }}
            title={`Threshold ${capture.thresholdDb} dBFS`}
            aria-hidden="true"
          />
        </div>
        <label className="radio-field radio-field--inline" htmlFor={ids.threshold}>
          <span id={ids.thresholdLabel} className="radio-field__label">Threshold</span>
          <input
            id={ids.threshold}
            aria-labelledby={ids.thresholdLabel}
            type="range"
            min={RADIO_THRESHOLD_MIN_DB}
            max={RADIO_THRESHOLD_MAX_DB}
            step={1}
            value={capture.thresholdDb}
            onChange={(e) => capture.setThresholdDb(Number(e.target.value))}
            aria-valuetext={`${capture.thresholdDb} dBFS`}
          />
          <span className="radio-field__value">{capture.thresholdDb} dBFS</span>
        </label>
      </div>

      <div className="radio-panel__actions">
        {capture.enabled ? (
          <button type="button" className="btn btn-danger radio-panel__toggle" onClick={capture.stop}>
            Stop listening
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-accent radio-panel__toggle"
            onClick={() => void capture.start()}
            disabled={!capture.supported || busy}
          >
            {busy ? "Starting…" : "Start listening"}
          </button>
        )}
        <span className="radio-panel__stats">
          {capture.sentFrames} frame{capture.sentFrames === 1 ? "" : "s"} sent
        </span>
        {shortcutsEnabled && (
          <span className="radio-panel__kbd-hint" aria-label="Keyboard shortcut">
            <kbd>Space</kbd> start/stop
          </span>
        )}
      </div>

      <TranscriptionLog lines={transcripts} onClear={onClearTranscripts} />
    </div>
  );
}
